import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldStar, ArrowDown, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const promote = async (u) => {
    if (!window.confirm(`Make ${u.name} (${u.email}) an admin? They'll be able to edit the programme, send announcements, and moderate Q&A.`)) return;
    try { await api.post(`/admin/users/${u.id}/promote`); toast.success(`${u.name} is now admin`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const demote = async (u) => {
    if (!window.confirm(`Remove admin from ${u.name}?`)) return;
    try { await api.post(`/admin/users/${u.id}/demote`); toast.success(`${u.name} is now an attendee`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?\n\nThis permanently removes their account, questions, poll votes, starred sessions, messages and push subscriptions. They will also be removed from the email allow-list so they can't re-register. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = users.filter((u) =>
    !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <p className="text-xs text-[#7A828C] mb-3">
        Admins can edit the programme, send announcements, run polls and moderate Q&amp;A. The 🗑 button permanently deletes a user&apos;s account and all their attached data - use only for test/dummy accounts or duplicates.
      </p>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="rounded-sm mb-3" data-testid="users-search" />
      <div className="space-y-2">
        {filtered.map((u) => (
          <div key={u.id} className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center gap-3" data-testid={`user-row-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{u.name}</span>
                {u.role === "admin" && <span className="font-mono-time text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#000000] text-[#FFFFFF] rounded-sm">Admin</span>}
              </div>
              <div className="text-xs text-[#7A828C] truncate">{u.email} {u.org ? `· ${u.org}` : ""}</div>
            </div>
            {u.role === "admin" ? (
              <Button size="sm" variant="outline" onClick={() => demote(u)} className="rounded-sm" data-testid={`demote-${u.id}`}><ArrowDown size={14} className="mr-1" />Remove admin</Button>
            ) : (
              <Button size="sm" onClick={() => promote(u)} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid={`promote-${u.id}`}><ShieldStar size={14} className="mr-1" />Make admin</Button>
            )}
            <button
              onClick={() => del(u)}
              aria-label={`Delete ${u.name}`}
              className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#FEECEC] text-[#DC4F48]"
              data-testid={`delete-user-${u.id}`}
            >
              <Trash size={16} />
            </button>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-[#7A828C] text-center py-6 text-sm">No users.</div>}
      </div>
    </div>
  );
}
